// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Billing Route
// apps/web/src/routes/billing.tsx
//
// Post-signup tier management. The SubscriptionBadge in the sidebar links
// here. Loads the current subscription and renders PlanSelectionPage in
// upgrade mode, so the user can switch to a different paid plan.
//
// 2026.4.9: Refactored for the locked-down /billing/create-checkout-session
// endpoint. The old flow passed tenant context (tenantId, billingEmail,
// subdomain) down to PlanSelectionPage, which called Stripe directly with a
// client-controlled DTO. That was a price-tampering hole; the API has since
// been tightened to read the tenant from the JWT and accept only
// { planId, billingCycle }. PlanSelectionPage is now pure presentational
// and this route owns the full upgrade-click → API-call → Stripe-redirect
// orchestration.
//
// 2026.10.1: With billing on ForgeMint, the owner's billing (plans, cards,
// invoices and receipts) shows above the plan picker in ForgeMint's own
// billing frame (@forgemint/embed), dark as ForgeTrack is. Card details go to
// ForgeMint's payment processor inside that frame, never through ForgeTrack.
// On Stripe, the page is as it was.
//
// 2026.10.2: With billing on ForgeMint, an upgrade's checkout shows here too, in
// ForgeMint's checkout frame (its embedded checkout), dark, in place of the plan
// picker, rather than on another site. Once paid, the page goes to
// /billing?checkout=success, as from the hosted checkout. On Stripe, the upgrade
// still redirects to Stripe's hosted checkout.
//
// Note: Free is not a valid upgrade target. The server rejects it with 400
// (see BillingController.cs), and PlanSelectionPage in upgrade mode disables
// the Free card's CTA. Downgrades live in a separate flow (not yet built).
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ForgeMintComponent } from '@forgemint/embed/react';
import {
  useSubscription,
  useCreateCheckoutSession,
  useBillingClientSecret,
  useEmbeddedCheckoutClientSecret,
  normalizeTier,
  tierToPlanId,
} from '../lib/api/billing';
import PlanSelectionPage from '../pages/signup/PlanSelectionPage';
import { PLANS, type PlanId, type BillingCycle } from '../pages/signup/plans';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';

export const Route = createFileRoute('/billing')({
  component: BillingPage,
});

function BillingPage() {
  const { data, loading, error: loadError } = useSubscription();
  const createCheckoutSession               = useCreateCheckoutSession();
  const fetchBillingClientSecret            = useBillingClientSecret();
  const fetchCheckoutSecret                 = useEmbeddedCheckoutClientSecret();
  const navigate                            = useNavigate();
  const [billingProblem, setBillingProblem] = useState<string | null>(null);
  const embeddedBilling                     = data?.embeddedBilling === true;

  // Billing on ForgeMint: the plan being paid for in ForgeMint's checkout frame, on this page.
  const [checkoutPlan, setCheckoutPlan]       = useState<{ planId: Exclude<PlanId, 'free'>; cycle: BillingCycle } | null>(null);
  const [checkoutProblem, setCheckoutProblem] = useState<string | null>(null);
  const fetchCheckoutClientSecret = useCallback(
    () => (checkoutPlan
      ? fetchCheckoutSecret(checkoutPlan.planId, checkoutPlan.cycle)
      : Promise.reject(new Error('No plan is chosen.'))),
    [checkoutPlan, fetchCheckoutSecret],
  );

  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [upgradeError, setUpgradeError]     = useState<string | null>(null);

  // ── Upgrade handler ────────────────────────────────────────────────────────
  // PlanSelectionPage (in upgrade mode) hands us the user's chosen plan +
  // cycle. We POST to /billing/create-checkout-session (Owner-only, reads
  // the tenant from the JWT) and redirect to the returned Stripe URL.
  //
  // Free is guarded out at the component layer too, but we assert here
  // so TypeScript can narrow the PlanId.
  const handleUpgrade = useCallback(
    async (planId: PlanId, cycle: BillingCycle) => {
      if (planId === 'free') {
        setUpgradeError('Free is not an upgrade target. Contact support for a downgrade.');
        return;
      }

      if (planId === 'enterprise') {
        const subject = encodeURIComponent('ForgeTrack Enterprise Upgrade Inquiry');
        const body = encodeURIComponent(
          `Hi ForgeTrack team,\n\nI'd like to upgrade my workspace to the Enterprise plan.\n\nPlease reach out to discuss custom pricing and capabilities.\n\nThank you`
        );
        window.open(`mailto:sales@forgetrack.io?subject=${subject}&body=${body}`, '_self');
        return;
      }

      // Billing on ForgeMint: the checkout shows here, in ForgeTrack's own page, rather than on another site.
      if (embeddedBilling) {
        setUpgradeError(null);
        setCheckoutProblem(null);
        setCheckoutPlan({ planId, cycle });
        window.scrollTo({ top: 0 });
        return;
      }

      setIsSubmitting(true);
      setUpgradeError(null);

      try {
        const { checkoutUrl } = await createCheckoutSession(planId, cycle);
        // Full-page redirect — leaves our SPA for Stripe's hosted checkout.
        // On success, Stripe returns the user to /settings/billing?checkout=success
        // per the server-side URL config.
        window.location.href = checkoutUrl;
      } catch (err: any) {
        const message =
          err?.response?.data?.error ??
          err?.response?.data?.message ??
          err?.message ??
          'Unable to open checkout. Please try again.';
        setUpgradeError(message);
        setIsSubmitting(false);
      }
      // Don't reset isSubmitting on success — we're about to navigate away
      // and resetting the flag would briefly un-spin the button.
    },
    [createCheckoutSession, embeddedBilling],
  );

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          minHeight:      '100vh',
          background:     C.bg,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          color:          C.muted,
          fontFamily:     F.mono,
          fontSize:       12,
          letterSpacing:  '0.12em',
          textTransform:  'uppercase',
        }}
      >
        Loading subscription…
      </div>
    );
  }

  // ── Backend error (500, network, timeout) ───────────────────────────────────
  // Distinguish real errors from 403 (non-owner). useSubscription sets error
  // for non-403 failures and returns null data for 403.
  if (loadError && !data) {
    return (
      <div
        style={{
          minHeight:      '100vh',
          background:     C.bg,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          padding:        24,
        }}
      >
        <div
          style={{
            maxWidth:     520,
            background:   C.panel,
            border:       `1px solid ${C.coral}40`,
            borderRadius: 14,
            padding:      32,
            textAlign:    'center',
            fontFamily:   F.display,
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div
            style={{
              fontSize:      11,
              fontFamily:    F.mono,
              color:         C.coral,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              marginBottom:  10,
            }}
          >
            Unable to load billing
          </div>
          <div style={{ fontSize: 16, color: C.text, fontWeight: 600, marginBottom: 8 }}>
            Something went wrong loading your subscription
          </div>
          <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.6, marginBottom: 16 }}>
            This is usually a temporary issue. If it persists, check that the API is running
            and Stripe is configured correctly.
          </div>
          <details style={{ textAlign: 'left', marginBottom: 20 }}>
            <summary
              style={{
                fontSize:   11,
                fontFamily: F.mono,
                color:      C.dim,
                cursor:     'pointer',
                marginBottom: 8,
              }}
            >
              Technical details
            </summary>
            <pre
              style={{
                fontSize:     11,
                fontFamily:   F.mono,
                color:        C.coral,
                background:   C.surface,
                borderRadius: 6,
                padding:      12,
                overflow:     'auto',
                maxHeight:    200,
                whiteSpace:   'pre-wrap',
                wordBreak:    'break-all',
              }}
            >
              {loadError.message}
            </pre>
          </details>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background:    'transparent',
              border:        `1px solid ${C.coral}50`,
              color:         C.coral,
              borderRadius:  8,
              padding:       '10px 20px',
              fontSize:      13,
              fontFamily:    F.display,
              fontWeight:    600,
              cursor:        'pointer',
              letterSpacing: '0.02em',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Forbidden / non-owner ──────────────────────────────────────────────────
  // useSubscription returns null data + no error for 403.
  if (!data) {
    return (
      <div
        style={{
          minHeight:      '100vh',
          background:     C.bg,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          padding:        24,
        }}
      >
        <div
          style={{
            maxWidth:     480,
            background:   C.panel,
            border:       `1px solid ${C.amber}40`,
            borderRadius: 14,
            padding:      32,
            textAlign:    'center',
            fontFamily:   F.display,
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
          <div
            style={{
              fontSize:      11,
              fontFamily:    F.mono,
              color:         C.amber,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              marginBottom:  10,
            }}
          >
            Owner access required
          </div>
          <div style={{ fontSize: 16, color: C.text, fontWeight: 600, marginBottom: 8 }}>
            Billing is managed by your workspace owner
          </div>
          <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.6, marginBottom: 24 }}>
            Only the workspace owner or an admin can view and change the subscription tier.
            Ask your owner to upgrade or invite you with the right role.
          </div>
          <button
            type="button"
            onClick={() => navigate({ to: '/dashboard' })}
            style={{
              background:    'transparent',
              border:        `1px solid ${C.amber}50`,
              color:         C.amber,
              borderRadius:  8,
              padding:       '10px 20px',
              fontSize:      13,
              fontFamily:    F.display,
              fontWeight:    600,
              cursor:        'pointer',
              letterSpacing: '0.02em',
            }}
          >
            ← Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Authorized — render the plan picker in upgrade mode ────────────────────
  // normalizeTier handles legacy "Starter" DB rows from before the PlanMatrix
  // fix, so existing dev tenants don't crash the picker.
  const currentTier   = normalizeTier(data.tier);
  const currentPlanId = tierToPlanId(currentTier);

  return (
    <>
      {data.embeddedBilling ? (
        // Above the picker's fixed background layers, which sit at z-index 0.
        <section style={{ position: 'relative', zIndex: 1, background: C.bg, padding: '32px 24px 8px' }}>
          <div style={{ maxWidth: 960, margin: '0 auto' }}>
            <div
              style={{
                fontSize:      11,
                fontFamily:    F.mono,
                color:         C.muted,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                marginBottom:  12,
              }}
            >
              Your billing
            </div>
            {billingProblem ? (
              <div style={{ fontSize: 13, color: C.coral, marginBottom: 12 }}>{billingProblem}</div>
            ) : null}
            <ForgeMintComponent
              component="billing"
              theme="dark"
              title="Your billing"
              host={import.meta.env.VITE_FORGEMINT_HOST || undefined}
              fetchClientSecret={fetchBillingClientSecret}
              onError={(problem) => setBillingProblem(problem.message)}
            />
          </div>
        </section>
      ) : null}
      {checkoutPlan ? (
        // The upgrade's checkout, in ForgeMint's frame, in place of the plan picker.
        <section style={{ position: 'relative', zIndex: 1, background: C.bg, padding: '8px 24px 48px', minHeight: '100vh' }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <button
              type="button"
              onClick={() => setCheckoutPlan(null)}
              style={{
                background:    'transparent',
                border:        'none',
                color:         C.muted,
                fontSize:      13,
                fontFamily:    F.display,
                cursor:        'pointer',
                padding:       '8px 0',
                marginBottom:  12,
              }}
            >
              ← Choose another plan
            </button>
            <div
              style={{
                fontSize:      11,
                fontFamily:    F.mono,
                color:         C.muted,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                marginBottom:  12,
              }}
            >
              Upgrade to {PLANS.find((plan) => plan.id === checkoutPlan.planId)?.name ?? checkoutPlan.planId} · {checkoutPlan.cycle}
            </div>
            {checkoutProblem ? (
              <div style={{ fontSize: 13, color: C.coral, marginBottom: 12 }}>{checkoutProblem}</div>
            ) : null}
            <ForgeMintComponent
              key={`${checkoutPlan.planId}-${checkoutPlan.cycle}`}
              component="checkout"
              theme="dark"
              title="Checkout"
              host={import.meta.env.VITE_FORGEMINT_HOST || undefined}
              fetchClientSecret={fetchCheckoutClientSecret}
              onError={(problem) => setCheckoutProblem(problem.message)}
            />
          </div>
        </section>
      ) : (
        <PlanSelectionPage
          mode="upgrade"
          currentPlanId={currentPlanId}
          onContinue={handleUpgrade}
          isSubmitting={isSubmitting}
          error={upgradeError}
        />
      )}
    </>
  );
}
